import React from "react";
import { Chip } from "@mui/material";
import moment from "moment";
import MDAvatar from "../../../../components/MDAvatar";
import MDBox from "../../../../components/MDBox";
import MDTypography from "../../../../components/MDTypography";
import {
    soOrderTypeMap,
    ticketPriorityOptions,
    ticketStatusOptions,
    ticketTypeOptions,
} from "../../../../constants";
import IconButton from "@mui/material/IconButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {onErrorAlert, onSuccessAlert} from "../../../../actions";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import LocalStorage from "../../../../utils/LocalStorage";



const TicketListItem = ({
                            ticket,
                            isSelected,
                            onClick,
                            assigneeList = []
                        }) => {

    const assignee = assigneeList?.find(
        (a) => a?.assigneeId === ticket?.assigneeId
    );
    const {t} = useTranslation()

    const dispatch = useDispatch()
    const handleCopy = (text) => {
        const textToCopy = text
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(textToCopy)
                .then(() => {
                    dispatch(onSuccessAlert(`"${textToCopy}" ${t('copied')}`))
                })
                .catch((err) => {
                    dispatch(onErrorAlert(`${t('copy failed')}`))
                });
        }

    };
    return (
        <MDBox
            onClick={onClick}
            sx={{
                p: 1,
                mb: 0.8,
                borderRadius: 1.5,
                cursor: "pointer",
                minWidth: 300,
                backgroundColor: isSelected ? "action.selected" : "transparent",
                border: "1px solid",
                borderColor: isSelected ? "primary.main" : "divider",

                transition: "all 0.15s",

                "&:hover": {
                    backgroundColor: "action.hover",
                },
            }}
        >
            {/* 第一行：ID + 时间 */}
            <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDTypography
                        variant="button"
                        fontWeight="medium"
                        color="info"
                    >
                        {ticket.ticketId}
                    </MDTypography>

                    {ticket?.ticketId && (
                        <IconButton
                            size="small"
                            sx={{
                                p: 0,
                                ml: 0,
                            }}
                            onClick={(e) => {
                                e.stopPropagation(); // 🚨 很关键
                                handleCopy(ticket?.ticketId);
                            }}
                        >
                            <ContentCopyIcon
                                sx={{
                                    width: 14,
                                    height: 14,
                                }}
                            />
                        </IconButton>
                    )}
                </MDBox>

                <MDBox display="flex" alignItems="center" gap={0.8}>
                    <MDTypography variant="caption" fontWeight="bold">
                        {moment(ticket.addTime).fromNow()}
                    </MDTypography>

                    <MDTypography variant="caption" fontSize="10px" color="text">
                        ({t('TicketManagementPage.added')})
                    </MDTypography>
                </MDBox>
            </MDBox>

            {/* 第二行：Order */}
            <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDTypography variant="caption" fontWeight="bold" color="primary">
                        {t('TicketManagementPage.Order No.')}: {ticket?.fromOrderNo || '-'}
                    </MDTypography>

                    {ticket?.fromOrderNo && (
                        <IconButton
                            size="small"
                            sx={{
                                p: 0,
                                ml: 0,   // 和文字更贴一点
                            }}
                            onClick={(e) => {
                                e.stopPropagation(); // 🚨 防止触发父点击
                                handleCopy(ticket?.fromOrderNo);
                            }}
                        >
                            <ContentCopyIcon sx={{
                                fontSize: '15px !important',
                            }} />
                        </IconButton>
                    )}
                </MDBox>
            </MDBox>

            {/* 第三行：用户 */}
            <MDBox
                display="flex"
                alignItems="center"
                gap={0.8}
                mt={0.5}
            >
                {/* Avatar */}
                <MDAvatar bgColor="light" size="sm" shadow="xxl">
                    {ticket.ownerName?.[0] || "U"}
                </MDAvatar>

                {/* Name + Type */}
                <MDBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                >
                    <MDTypography fontWeight="medium" variant="button">
                        {LocalStorage.getItem('role') === 'admin'?ticket?.ownerName:ticket?.addWho}
                    </MDTypography>

                    {/* 两个 chip 横向排列 */}
                    <MDBox display="flex" gap={0.5} mt={0.3}>
                        {/* 原来的 ticketType */}
                        <Chip
                            size="small"
                            label={
                                t(
                                    `TicketManagementPage.${
                                        ticketTypeOptions.find(
                                            (i) => i.value === ticket.ticketType
                                        )?.label
                                    }`
                                ) || "-"
                            }
                            variant="outlined"
                            sx={{
                                height: 18,
                                fontSize: 10,
                                fontWeight: 900,
                            }}
                        />

                        {/* 新增 soOrderType */}
                        {
                            soOrderTypeMap[ticket.soOrderType] &&
                            <Chip
                                size="small"
                                label={t(`TicketManagementPage.${soOrderTypeMap[ticket.soOrderType]}`)}
                                sx={{
                                    height: 18,
                                    fontSize: 10,
                                    fontWeight: 900,
                                }}
                            />
                        }

                    </MDBox>
                </MDBox>
            </MDBox>

            {/* 第四行：subject */}
            <MDBox display="flex" alignItems="center" mt={0.5}>
                <MDTypography
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                    fontWeight="medium"
                    variant="caption"
                >
                    {ticket.ticketSubject || "-"}
                </MDTypography>
            </MDBox>

            {/* 第五行：chips */}
            <MDBox
                mt={0.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                {/* 左边：status + priority */}
                <MDBox display="flex" gap={0.5} flexWrap="wrap">
                    <Chip
                        size="small"
                        color={
                            ticketStatusOptions.find(
                                (i) => i.value === ticket.ticketStatus
                            )?.color || "default"
                        }
                        label={
                            t(
                                `TicketManagementPage.${
                                    ticketStatusOptions.find(
                                        (i) => i.value === ticket.ticketStatus
                                    )?.label || ""
                                }`
                            ) || "-"
                        }
                        sx={{
                            height: 18,
                            fontSize: 10,
                            fontWeight: 900,
                        }}
                    />

                    <Chip
                        size="small"
                        color={
                            ticketPriorityOptions.find(
                                (i) => i.value === ticket.ticketPriority
                            )?.color || "default"
                        }
                        label={
                            (() => {
                                const label = ticketPriorityOptions.find(
                                    (i) => i.value === ticket.ticketPriority
                                )?.label;

                                return label ? t(`TicketManagementPage.${label}`) : "-";
                            })()
                        }
                        sx={{
                            height: 18,
                            fontSize: 10,
                            fontWeight: 900,
                        }}
                    />
                </MDBox>

                {/* 右边：assigneeId */}
                <MDBox
                    sx={{
                        px: 0.8,
                        py: 0.2,
                        border: "1px solid",
                        borderColor: assignee ? "info.main" : "grey.400",
                        borderRadius: 1.5,
                        fontSize: 10,
                        fontWeight: 600,
                        color: assignee ? "info.main" : "grey.500",
                        backgroundColor: "transparent",
                        lineHeight: 1.2,
                    }}
                >
                    {assignee?.assigneeName || t("TicketManagementPage.unassigned")}
                </MDBox>
            </MDBox>
        </MDBox>
    );
};

export default React.memo(TicketListItem);